import React from 'react';
import { useLocation } from 'wouter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../hooks/use-auth';
import { ArrowLeft, Lock, Eye, EyeOff } from 'lucide-react';
import { queryClient } from '../lib/queryClient';

export default function SimpleLogin() {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const { loginMutation } = useAuth();
  const [, setLocation] = useLocation();
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    try {
      await loginMutation.mutateAsync({ email, password });
      setLocation('/dashboard');
    } catch (err) {
      try {
        const res = await fetch('/api/simple-login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password })
        });
        if (!res.ok) throw new Error(await res.text());
        const user = await res.json();
        queryClient.setQueryData(['/api/user'], user);
        setLocation('/dashboard');
      } catch (fallbackErr: any) {
        setErrorMsg(fallbackErr?.message || 'Login failed');
      }
    }
  };

  // Note: Navigation is now handled by the auth hook after login success
  // This prevents navigation conflicts and React errors

  // Force light mode on login page
  React.useEffect(() => {
    document.documentElement.classList.remove('dark');
    document.documentElement.classList.add('light');
    return () => {
      // Restore theme when leaving page
      const savedTheme = localStorage.getItem('remodra-theme');
      if (savedTheme === 'dark') {
        document.documentElement.classList.remove('light');
        document.documentElement.classList.add('dark');
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-slate-50 to-white">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto space-y-6">
          {/* Header Card */}
          <Card className="bg-white border-slate-200 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-slate-900">
                    <Lock className="h-6 w-6 text-green-500" />
                    Sign In
                  </CardTitle>
                  <p className="text-slate-600 mt-2">
                    Welcome back to Remodra
                  </p>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => setLocation('/')}
                  className="text-slate-600 hover:text-green-600"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Home
                </Button>
              </div>
            </CardHeader>
          </Card>

          {/* Login Form */}
          <Card className="bg-white border-slate-200 shadow-lg">
            <CardHeader>
              <CardTitle className="text-center text-slate-900">
                Sign In to Your Account
              </CardTitle>
              <p className="text-center text-slate-600">
                Enter your credentials to access your dashboard
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full bg-white text-slate-900 border-slate-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full pr-10 bg-white text-slate-900 border-slate-300"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                {errorMsg && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{errorMsg}</p>
                  </div>
                )}
                
                <Button
                  type="submit"
                  className="w-full remodra-button"
                  disabled={loginMutation.isPending}
                >
                  {loginMutation.isPending ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Signing in...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Lock className="h-4 w-4 mr-2" />
                      Sign In
                    </div>
                  )}
                </Button>
              </form>

              <div className="relative mt-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">Or</span>
                </div>
              </div>

              <div className="mt-6 space-y-2">
                <Button
                  variant="outline"
                  onClick={() => setLocation('/register')}
                  className="w-full border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                  Create New Account
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setLocation('/pricing')}
                  className="w-full text-slate-600 hover:text-green-600"
                >
                  View Pricing Plans
                </Button>
              </div>

              <div className="mt-6 text-center">
                <Button 
                  onClick={() => setLocation('/password-reset-request')}
                  variant="ghost"
                  className="text-slate-600 hover:text-green-600 text-sm"
                >
                  Forgot your password?
                </Button>
              </div>


            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}